import {
  createContext,
  Dispatch,
  KeyboardEventHandler,
  MouseEventHandler,
  SetStateAction,
  useCallback,
  useContext,
} from 'react';

export const DropdownContext = createContext<{
  activeDropdown: string | null;
  setActiveDropdown: Dispatch<SetStateAction<string | null>>;
}>({
  activeDropdown: null,
  setActiveDropdown: () => {},
});

let timeout: NodeJS.Timeout | null = null;

const useDropdown = (id: string, isSideMenu?: boolean) => {
  const { activeDropdown, setActiveDropdown } = useContext(DropdownContext);

  const handleOnMouseDown = useCallback<MouseEventHandler>(
    e => {
      e.preventDefault();
      if (isSideMenu && activeDropdown === id) {
        setActiveDropdown(null);
      } else {
        setActiveDropdown(id);
      }
    },
    [activeDropdown, setActiveDropdown, id, isSideMenu],
  );

  const handleOnKeyDown = useCallback<KeyboardEventHandler<any>>(
    e => {
      if (e.key === ' ' || e.key === 'Enter') {
        e.preventDefault();
        if (isSideMenu && activeDropdown === id) {
          setActiveDropdown(null);
        } else {
          setActiveDropdown(id);
        }
      }
    },
    [activeDropdown, setActiveDropdown, id, isSideMenu],
  );

  const handleOnMouseLeave = useCallback(() => {
    timeout = setTimeout(() => {
      if (activeDropdown === id) {
        setActiveDropdown(null);
      }
    }, 500);
  }, [setActiveDropdown, activeDropdown, id]);

  const handleOnMouseEnter = useCallback(() => {
    if (timeout) {
      clearTimeout(timeout);
      timeout = null;
    }
    setActiveDropdown(id);
  }, [setActiveDropdown, id]);

  return {
    open: activeDropdown === id,
    handlers: {
      handleOnKeyDown,
      handleOnMouseDown,
      handleOnMouseEnter,
      handleOnMouseLeave,
    },
  };
};

export default useDropdown;
